import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import GuardianLinksModal from '../components/GuardianLinksModal';
import AdminPage, { personTarget } from '../components/AdminShell';

const ROLE_LABELS = {
  athlete: 'Athlete',
  base_user: 'Base User',
  player_parent: 'Athlete',
  coach: 'Coach',
  basic_coach: 'Coach (Pending)',
  partner: 'Partner',
  guardian: 'Guardian',
  admin: 'Admin',
  internal_staff: 'Internal Staff',
  marketing: 'Marketing',
};

// Server timestamps are UTC but serialized without a tz suffix (e.g.
// "2026-05-31T17:45:00"). JS parses a tz-less datetime as LOCAL, which shows
// the UTC wall-clock verbatim instead of converting. Append 'Z' so it parses
// as UTC; toLocale* then renders in the viewing browser's local timezone.
const parseServerDate = (iso) => {
  const hasTz = /([zZ])|([+-]\d{2}:?\d{2})$/.test(iso);
  return new Date(hasTz ? iso : `${iso}Z`);
};

const formatDate = (iso) => {
  if (!iso) return null;
  return parseServerDate(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

const formatDateTime = (iso) => {
  if (!iso) return null;
  const d = parseServerDate(iso);
  const date = d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return `${date}, ${time}`;
};

// `key` is the sort column name sent to GET /admin/users — it must match a
// key in _USER_SORT_COLUMNS on the backend. Sorting is done server-side, over
// the whole user table, because this report only ever holds one page at a
// time: re-sorting the loaded rows in JS would cap "most recent login" at
// "most recent among the rows already fetched".
const COLUMNS = [
  { key: 'last_name',       label: 'User' },
  { key: 'role',            label: 'Role' },
  { key: 'created_at',      label: 'Signed up' },
  { key: 'first_login_at',  label: 'First login' },
  { key: 'last_login_at',   label: 'Last login' },
  { key: 'login_count',     label: 'Logins', align: 'right' },
];

const PAGE_SIZE = 50;

// `embedded` renders the table WITHOUT the admin shell — /admin/reports mounts
// this component as its "Logins" tab and has already drawn the sidebar and
// header. Same arrangement as AdminSignupsReportPage.
export default function AdminLoginsReportPage({ embedded = false }) {
  const { user, API_URL } = useAuth();
  const navigate = useNavigate();
  // The Admin home's Login problems queue used to link here with ?issues=1; it
  // has its own page now, so old links go there instead of mixing the two lists.
  const [searchParams] = useSearchParams();
  const legacyIssuesLink = searchParams.get('issues') === '1';
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState('');
  // Default to most-recent login first — admins land on the report to see who
  // was active last, not who signed up first.
  const [sortKey, setSortKey] = useState('last_login_at');
  const [sortDir, setSortDir] = useState('desc');
  const [guardianModal, setGuardianModal] = useState(null);

  const getConfig = useCallback(() => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
  }), []);

  useEffect(() => {
    if (user && user.role !== 'admin' && user.role !== 'internal_staff') {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  // Server-side pagination AND sorting: passing limit/offset makes
  // /admin/users return {items, total, has_more} instead of the full-table
  // array, and sort/sort_dir apply the ORDER BY before the LIMIT so page 1 is
  // the true global top 50. Loading 50 at a time keeps the initial render fast
  // on large user counts.
  const fetchUsers = useCallback(async ({ offset = 0, append = false } = {}) => {
    if (append) setLoadingMore(true); else setLoading(true);
    setError('');
    try {
      const res = await axios.get(
        `${API_URL}/admin/users?limit=${PAGE_SIZE}&offset=${offset}`
          + `&sort=${encodeURIComponent(sortKey)}&sort_dir=${sortDir}`,
        getConfig()
      );
      const data = res.data;
      const items = Array.isArray(data) ? data : (data?.items || []);
      const tot = Array.isArray(data) ? data.length : (data?.total || 0);
      const more = Array.isArray(data) ? false : !!data?.has_more;
      setUsers((prev) => (append ? [...prev, ...items] : items));
      setTotal(tot);
      setHasMore(more);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to load users');
    } finally {
      if (append) setLoadingMore(false); else setLoading(false);
    }
  }, [API_URL, getConfig, sortKey, sortDir]);

  // Refetches from offset 0 whenever the sort changes (fetchUsers is keyed on
  // sortKey/sortDir), so a re-sort pulls a fresh global page 1 rather than
  // reshuffling the rows already on screen.
  useEffect(() => {
    fetchUsers({ offset: 0, append: false });
  }, [fetchUsers]);

  const loadMore = () => {
    if (!hasMore || loadingMore) return;
    fetchUsers({ offset: users.length, append: true });
  };

  const handleSort = (key) => {
    if (key === sortKey) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      // First click on a new column: timestamps + counts default to DESC
      // (most-recent / highest first); text fields default to ASC.
      const isTextCol = key === 'last_name' || key === 'role';
      setSortDir(isTextCol ? 'asc' : 'desc');
    }
  };

  const sortIndicator = (key) => (
    key === sortKey
      ? <span className="adm-sort adm-sort--on">{sortDir === 'asc' ? '↑' : '↓'}</span>
      : <span className="adm-sort">↕</span>
  );

  // Loading and error are rendered INSIDE the shell rather than short-circuiting
  // the whole component. The old early returns dropped the sidebar and the page
  // title on every slow fetch, so the frame flickered in and out on each sort.
  const body = (
    <>
      {error && <div className="adm-error">{error}</div>}
      {loading ? (
        <div className="adm__card"><div className="adm-loading">Loading logins report…</div></div>
      ) : (
        <div className="adm__card adm__card--flush">
          <div className="adm-tablewrap">
            <table className="adm-table">
              <thead>
                <tr>
                  {COLUMNS.map((c) => (
                    <th
                      key={c.key}
                      className={`adm-th--sortable${c.align === 'right' ? ' adm-th--right' : ''}`}
                      onClick={() => handleSort(c.key)}
                      title="Click to sort"
                      aria-sort={sortKey === c.key ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
                    >
                      {c.label}{sortIndicator(c.key)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {users.length === 0 && (
                  <tr><td colSpan={COLUMNS.length}><div className="adm-empty">No users yet.</div></td></tr>
                )}
                {users.map((u) => {
                  const name = `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email;
                  // This report lists EVERY user, so most rows here are not
                  // athletes. personTarget keeps coach/partner/staff as plain
                  // text instead of a link to a profile that does not exist.
                  const target = personTarget(u.role);
                  return (
                    <tr key={u.id}>
                      <td>
                        {target ? (
                          <button
                            type="button"
                            className="adm-link adm-cell__name"
                            onClick={() => (target === 'guardian'
                              ? setGuardianModal(u)
                              : navigate(`/athletes/${u.id}`))}
                            title={target === 'guardian'
                              ? `View athletes linked to ${name}`
                              : `View ${name}'s full profile`}
                          >
                            {name}
                            {target === 'guardian' && <span className="adm-chip adm-chip--amber" style={{ marginLeft: 6 }}>linked athletes</span>}
                          </button>
                        ) : (
                          <span className="adm-cell__name">{name}</span>
                        )}
                        <div className="adm-cell__meta">{u.email}</div>
                      </td>
                      <td>{ROLE_LABELS[u.role] || u.role}</td>
                      <td title={u.created_at || ''}>{formatDate(u.created_at) || '—'}</td>
                      <td title={u.first_login_at || ''}>
                        {formatDate(u.first_login_at) || <span className="adm-never">Never</span>}
                      </td>
                      <td title={u.last_login_at || ''}>{formatDateTime(u.last_login_at) || '—'}</td>
                      <td className="adm-td--num">{u.login_count ?? 0}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {hasMore && (
            <div className="adm-pager">
              <button
                type="button"
                className="adm-btn"
                onClick={loadMore}
                disabled={loadingMore}
              >
                {loadingMore ? 'Loading…' : `Load more (${total - users.length} remaining)`}
              </button>
            </div>
          )}
        </div>
      )}

      <GuardianLinksModal
        guardian={guardianModal}
        onClose={() => setGuardianModal(null)}
        onOpenAthlete={(athleteId) => { setGuardianModal(null); navigate(`/athletes/${athleteId}`); }}
      />
    </>
  );

  if (legacyIssuesLink) return <Navigate to="/admin/login-problems" replace />;

  const shown = `Showing ${users.length}${total > 0 ? ` of ${total}` : ''} users`;

  if (embedded) {
    return (
      <>
        <div className="adm-toolbar">
          <span className="adm-note" style={{ margin: 0 }}>{shown}</span>
        </div>
        {body}
      </>
    );
  }

  return (
    <AdminPage active="reports" title="Logins" subtitle={shown}>
      {body}
    </AdminPage>
  );
}
